import EventEmitter from "events";
import { CCSetup } from "./CCSetup";
import { trySortByKeyEnding } from "./CCUtility";
import {
  CCTable,
  CCTableAddRowButton,
  CCTableRemoveRowButton
} from "./components/Table";
import { CCIcon } from "./components/UI";
import {
  CCInteractiveTableColumn,
  CCInteractiveTableEventMap,
  CCInteractiveTableOptions,
  TableDataRow,
  _defaultTableOptions
} from "./types/table";

export class CCInteractiveTable<T extends string = string> extends EventEmitter<
  CCInteractiveTableEventMap<T>
> {
  public container: JQuery;
  public cc: CCSetup;
  public baseClass: string;
  public dataPrefix: string;
  public dataKey: string;
  public columns: CCInteractiveTableColumn<T>[];
  public options: CCInteractiveTableOptions<T>;
  public initialHTML: string;

  /**
   * @param {JQuery} container - The container that the table will be in.
   * @param {CCSetup} cc - The CCSetup object that is controlling the conference.
   * @param {string} baseClass - The base css/html class to add to DOM objects.
   * @param {string} dataPrefix - The database data prefix.
   * @param {string} dataKey - The database data key.
   * @param {CCInteractiveTableColumn<T>[]} columns - The table columns.
   * @param {CCInteractiveTableOptions} options - Table options.
   * @param [options.editable] - Editability of table features
   * @param [options.headerBackground="#DDDDDD"] - The background color of the table header.
   * @param [options.padding="5px"] - The padding inside each cell.
   * @param [options.removeRowButton='right'] - The location of the "X" remove row button. Empty string for no button.
   * @param [options.addRowButton=true] - True if the add row "+" button should be added.
   * @param [options.border="1px solid #DDDDDD"] - The table and cell border.
   * @param [options.autoAppend=true] - True if the table should be automatically appended to the DOM.
   * @param [options.widthOverride="1020px"] - Override the full table width (rarely used in cases where there are many columns)
   * @param [options.bodyID=] - Table body HTML element ID. Infered to be "{baseClass}Body".
   * @param [options.addRowClass=] - HTML element class of the add row button. Infered to be "{baseClass}AddRow".
   * @param [options.removeRowClass=] - HTML element class of the remove row buttons. Infered to be "{baseClass}RemoveRow".
   * @param [options.richTextEnabled=false] - Is rich text editing enabled?
   */
  constructor(
    container: JQuery,
    cc: CCSetup,
    baseClass: string,
    dataPrefix: string,
    dataKey: string,
    columns: CCInteractiveTableColumn<T>[],
    options: CCInteractiveTableOptions<T> = _defaultTableOptions as any
  ) {
    super();
    this.options = options;
    this.container = container;
    this.cc = cc;

    const editability = this.options.editable;
    // Editability should override these options.
    if (editability.disableEverything || !editability.allowRemovingRows) {
      // Only override default if not specified
      this.options.removeRowButton = Boolean(options.removeRowButton)
        ? options.removeRowButton
        : "";
    }
    // Editability should override these options.
    if (editability.disableEverything || !editability.allowAddingRows) {
      // Only override default if not specified
      this.options.addRowButton = Boolean(options.addRowButton)
        ? options.addRowButton
        : false;
    }

    // Assign any undefined to defaults
    Object.keys(_defaultTableOptions).forEach(
      (key) => (this.options[key] ??= _defaultTableOptions[key])
    );

    const countOfHidden = columns.reduce(
      (acc, curr) => (curr.hidden == true ? acc + 1 : acc),
      0
    );
    if (
      columns.every(
        (column) => column.hidden == true || column.width == undefined
      )
    ) {
      columns.forEach((column) => {
        if (!column.hidden) {
          column.width =
            String(Math.floor(100 / (columns.length - countOfHidden))) + "%";
        }
      });
    } else if (
      columns.some(
        (column) => column.hidden != true && column.width == undefined
      )
    ) {
      let remainingSpace = 100;
      const undefinedWidths = [];
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].hidden) continue;
        const intWidth = parseInt(columns[i].width);
        isNaN(intWidth)
          ? undefinedWidths.push(i)
          : (remainingSpace -= intWidth);
      }
      undefinedWidths.forEach((index) => {
        columns[index].width =
          String(Math.floor(remainingSpace / undefinedWidths.length)) + "%";
      });
    }

    this.columns = columns;
    this.baseClass = baseClass;
    this.dataKey = dataKey;
    this.dataPrefix = dataPrefix;
    this.initialHTML = CCTable(
      this.columns.filter((c) => !c.hidden),
      {
        bodyID: `${baseClass}Body`,
        addRowClass: `${baseClass}AddRow`,
        removeRowClass: `${baseClass}RemoveRow`,
        ...options
      }
    );

    if (options.autoAppend) container.append(this.initialHTML);
    if (options.removeRowButton) this.hookRemoveRowButtons();
    if (options.addRowButton) this.hookAddRowButton();
    if (options.editable.allowEditingText) this.hookDataEdit();
  }

  /**
   * For each row in the database, calls `update`.
   * Sets rows to an array of each row returned from `update`
   *
   * `updateRows` will call `add` after calling `update` with each row,
   * and append the return value of `add` to the rows.
   */
  public updateRows(
    update: (
      row: TableDataRow<typeof this.columns>,
      index: number
    ) => TableDataRow<typeof this.columns>,
    add: () => TableDataRow<typeof this.columns>[] = () => [],
    hashOnAdd: boolean = false
  ) {
    const updated = [...this.rows.map((row, i) => update(row, i)), ...add()];
    this.clear();
    this.bulkAddRowsFromData(updated, hashOnAdd);
  }
  /**
   *  Returns each row of data in the database for this table
   */
  public get rows(): TableDataRow<typeof this.columns>[] {
    this.cc.newDef.career[this.cc.viewYear].data ??= {};
    const yearData = this.cc.newDef.career[this.cc.viewYear].data;
    const data = Object.values(yearData[this.dataPrefix + this.dataKey] ?? {});
    return data.filter(Boolean) as TableDataRow<typeof this.columns>[];
  }

  /**
   *  Clear the table in the UI and in the database.
   */
  public clear(): void {
    delete this.cc.newDef.career[this.cc.viewYear].data[
      this.dataPrefix + this.dataKey
    ];
    this.cc.emptyHTMLElement($(`#${this.baseClass}Body`));
    this.emit("rowCountChange");
    this.cc.checkSave(this.cc.quietFields);
  }

  /**
   *  Clear unedited rows from the table in the UI and in the database.
   */
  public clearUnedited(): void {
    const data = this.cc.newDef.career[this.cc.viewYear].data;
    const dataToKeep = [];
    $(`#${this.baseClass}Body`)
      .children()
      .each((_index, childElement) => {
        const row = $(childElement);
        const rowId = row.attr("id");
        const rowData = data[this.dataPrefix + this.dataKey][rowId];
        if (rowData != undefined) {
          if (rowData.initialHash != rowData.editHash) {
            dataToKeep.push(rowData);
          }
        }
      });
    delete data[this.dataPrefix + this.dataKey];
    data[this.dataPrefix + this.dataKey] = {};
    for (let i = 0; i < dataToKeep.length; ++i) {
      const rowKey = `${this.dataKey}-${i}`;
      data[this.dataPrefix + this.dataKey][rowKey] = dataToKeep[i];
    }
    this.populateFromDatabase();
    this.emit("rowCountChange");
    this.cc.checkSave(this.cc.quietFields);
  }

  /**
   *  Populate the table with data from the database.
   */
  public populateFromDatabase(): void {
    // Reference to career conf data
    const existingData = this.cc.newDef.career[this.cc.viewYear].data;
    // If custom-supervise is undefined, set it to {}
    if (existingData[this.dataPrefix + this.dataKey] == undefined)
      existingData[this.dataPrefix + this.dataKey] = {};
    // Get existing data keys
    const existingKeys = Object.keys(
      existingData[this.dataPrefix + this.dataKey] || {}
    ).filter((key) => {
      return Boolean(existingData[this.dataPrefix + this.dataKey][key]);
    });
    // Empty out the table body
    const tableBody = $(`#${this.baseClass}Body`);
    this.cc.emptyHTMLElement(tableBody);
    // If we have data
    if (existingKeys.length) {
      existingKeys.sort(trySortByKeyEnding);
      const rows = [];
      // For each entry in our data
      existingKeys.forEach((entryKey) => {
        const entry = existingData[this.dataPrefix + this.dataKey][entryKey];
        if (entry != undefined) {
          // If all entries are falsey
          if (
            Object.keys(entry).every((k) => !Boolean(entry[k])) ||
            Object.keys(entry).length == 0
          ) {
            delete existingData[this.dataPrefix + this.dataKey][entryKey];
          } else {
            // Add a row with the data
            rows.push([entryKey, entry]);
          }
        }
      });
      tableBody.html(rows.map(([k, v]) => this.generateRow(k, v)).join(""));
    }
    this.emit("dataRefresh");
  }

  /**
   * Disable all editing of the table
   */
  public disableEditing() {
    this.options.editable.disableEverything = true;
    this.container.off();
    if (Boolean(this.options.addRowButton)) {
      const tableElement = $(`#${this.baseClass}Body`).parent();
      tableElement.children("tfoot").first().remove();
    }
    if (Boolean(this.options.removeRowButton)) {
      const theadRow = $(`#${this.baseClass}Body`)
        .parent()
        .children("thead")
        .first()
        .children("tr")
        .first();
      theadRow.children(".removeRowHead").remove();
      if (this.options.removeRowButton === "left") {
        theadRow.prepend(CCTableRemoveRowButton());
      } else {
        theadRow.append(CCTableRemoveRowButton());
      }
      const rows = $(`#${this.baseClass}Body`).children("tr");
      rows.children(`.${this.baseClass}RemoveRow`).remove();
    }
    const selects = this.container.find("select");
    selects.attr("disabled", "disabled");
    selects.prop("disabled", true);
    const inputs = this.container.find("input");
    inputs.attr("disabled", "disabled");
    inputs.prop("disabled", true);
  }
  /*
   * Enable editing of the table, usually after a disableEditing()
   *
   */
  public enableEditing() {
    this.options.editable.disableEverything = false;
    const selects = this.container.find("select");
    selects.removeAttr("disabled");
    selects.removeProp("disabled");
    const inputs = this.container.find("input");
    inputs.removeAttr("disabled");
    inputs.removeProp("disabled");
    if (this.options.removeRowButton) {
      // Normalize and add column for remove button
      const theadRow = $(`#${this.baseClass}Body`)
        .parent()
        .children("thead")
        .first()
        .children("tr")
        .first();
      theadRow.children(".removeRowHead").remove();
      if (this.options.removeRowButton === "left") {
        theadRow.prepend(CCTableRemoveRowButton());
      } else {
        theadRow.append(CCTableRemoveRowButton());
      }

      // Normalize and add remove button to each row
      const rows = $(`#${this.baseClass}Body`).children("tr");
      rows.children(`.${this.baseClass}RemoveRow`).remove();
      if (this.options.removeRowButton === "left") {
        rows.prepend(
          `<td class="${this.baseClass}RemoveRow noPrint">${CCIcon("remove")}</td>`
        );
      } else {
        rows.prepend(
          `<td class="${this.baseClass}RemoveRow noPrint">${CCIcon("remove")}</td>`
        );
      }
      this.hookRemoveRowButtons();
    }
    if (this.options.addRowButton) {
      const tableElement = $(`#${this.baseClass}Body`).parent();
      tableElement.children("tfoot").first().remove();
      tableElement.append(
        CCTableAddRowButton(
          this.columns.filter((c) => !c.hidden),
          {
            addRowClass: `${this.baseClass}AddRow`,
            ...this.options
          }
        )
      );
      this.hookAddRowButton();
    }
    this.hookDataEdit();
  }

  /**
   * Refresh the tinymce instance to watch new td elements
   */
  public refreshRichTextInstances() {
    if (
      this.options.richTextEnabled &&
      !this.options.editable.disableEverything
    ) {
      tinymce.remove("tr td.richTextEnabled");
      tinymce.init({
        selector: "tr td.richTextEnabled",
        height: "inherit",
        inline: true,
        menubar: false,
        statusbar: false,
        plugins: "paste link",
        paste_word_valid_elements: "b,strong,i,u,em,br,sup,sub,p",
        toolbar:
          "bold italic underline subscript superscript | link unlink | removeformat",
        default_link_target: "_blank",
        entity_encoding: "raw",
        content_style: "p { margin: 0px 0px 0px 0px; }",
        force_br_newlines: false,
        force_p_newlines: false,
        forced_root_block: "",
        formats: {
          underline: { inline: "u", exact: true }
        },
        setup: (editor: any) => {
          editor.on("change", (event: Event) => {
            $(editor.bodyElement).trigger("input");
            event.stopImmediatePropagation();
            event.stopPropagation();
          });
          editor.on("input", (event: Event) => {
            $(editor.bodyElement).trigger("input");
            event.stopImmediatePropagation();
            event.stopPropagation();
          });
          editor.on("focusout", (event: Event) => {
            $(editor.bodyElement).trigger("input");
            event.stopImmediatePropagation();
            event.stopPropagation();
          });
        }
      });
    }
  }

  public getRowIndexes() {
    const data = this.cc.newDef.career[this.cc.viewYear].data;
    const keys = [];
    for (const key of Object.keys(data[this.dataPrefix + this.dataKey])) {
      if (key != undefined) {
        const last = key.split("-").pop();
        if (Boolean(last) && !isNaN(+last)) {
          keys.push(+last);
        }
      }
    }
    return keys;
  }
  public getLastRowIndex() {
    return Math.max(...this.getRowIndexes());
  }
  /**
   *  Add data that will not be shown or editable in a column.
   *  Like addPseudoColumn, except the data doesn't change.
   */
  public addHiddenData(linkedRowIndex: number, key: string, value: any) {
    const data = this.cc.newDef.career[this.cc.viewYear].data;
    data[this.dataPrefix + this.dataKey] ??= {};
    data[this.dataPrefix + this.dataKey][this.dataKey + "-" + linkedRowIndex][
      key
    ] = value;
  }
  /**
   *  Add data in the database that is linked to a column in the table.
   *  {callback} is called when the column is edited.
   */
  public addPseudoColumn(
    linkedTo: RegExp,
    dbKey: string,
    callback: (value: any, event?: any) => any,
    skipInitialUpdate: boolean = false
  ) {
    this.on("edit", (event) => {
      if (linkedTo.test(event.column)) {
        const val = event.value || "";
        const data = this.cc.newDef.career[this.cc.viewYear].data;
        data[this.dataPrefix + this.dataKey] ??= {};
        data[this.dataPrefix + this.dataKey][event.row] ??= {};
        data[this.dataPrefix + this.dataKey][event.row][dbKey] = callback(
          val,
          event
        );
      }
    });

    if (!skipInitialUpdate) {
      $(`#${this.baseClass}Body`)
        .children()
        .each((_index, rowElement) => {
          const row = $(rowElement);
          row.children().each((_index: number, cellElement) => {
            if (
              _index < this.columns.length &&
              linkedTo.test(this.columns[_index].db)
            ) {
              const target = $(cellElement);
              this.emit("edit", {
                element: target,
                row: row.attr("id"),
                column: this.columns[_index].db,
                value: target.text()
              });
            }
          });
        });
    }
  }

  // The same as addRowsFromData but takes an array of data objects instead of a single object.
  // See addRowsFromData docs for more info.
  public bulkAddRowsFromData(
    data: Record<T, string>[],
    hashRows: boolean = false
  ): void {
    data = data.filter(Boolean);
    const tableBody = $(`#${this.baseClass}Body`);

    if (tableBody.html().trim() == "<tr></tr>") tableBody.empty();
    // Generate new row's key
    let baseRowKey = tableBody.children().length;

    const viewYearData = (this.cc.newDef.career[this.cc.viewYear].data ??= {});
    const ccDataRef = (viewYearData[this.dataPrefix + this.dataKey] ??= {});

    const generatedRows = new Array(data.length);
    let index = 0;
    for (const dataEntry of data) {
      const rowKey = `${this.dataKey}-${baseRowKey}`;
      generatedRows[index++] = this.generateRow(rowKey, dataEntry);
      ccDataRef[rowKey] ??= {};
      for (const key of Object.keys(dataEntry)) {
        ccDataRef[rowKey][key] = dataEntry[key];
      }

      if (hashRows) {
        ccDataRef[rowKey].initialHash = ccDataRef[rowKey].editHash =
          this.quickRowHash(ccDataRef[rowKey]);
      }
      baseRowKey++;
    }
    tableBody.append(generatedRows.join(""));
    this.emit("rowCountChange");
    this.cc.checkSave(this.cc.quietFields);
  }
  /**
   *  Add a row based on given data.
   *  Data should be an object with keys that match the tables columns[].db keys.
   *
   *  example:
   *
   *  ```ts
   *  // Define table columns
   *  const columns = [
   *    { display: "Text", db: "key" },
   *    { display: "Other", db: "anotherKey" }
   *  ];
   *  // Create table
   *  const table = new CCInteractiveTable(
   *    // ... ,
   *    columns,
   *    // ...
   *  );
   *
   *  // To call addRowFromData on this table:
   *  table.addRowFromData({
   *    key: "Value that would show in the first column in the columns array",
   *    // ^ 'key' taken from the 'db' property of the first entry in the columns array.
   *    anotherKey: "Value that would show in the second column in the columns array"
   *    // ^ 'anotherKey' taken from the 'db' property of the second entry in the columns array.
   *  });
   *  ```
   *  It could help to use `InferedRowType<typeof columns>`
   */
  public addRowFromData(data: Record<T, string>, hashRow: boolean = true) {
    const tableBody = $(`#${this.baseClass}Body`);

    if (tableBody.html().trim() == "<tr></tr>") tableBody.empty();
    // Generate new row's key
    const rowKey = `${this.dataKey}-${tableBody.children().length}`;

    const viewYearData = (this.cc.newDef.career[this.cc.viewYear].data ??= {});
    const ccDataRef = (viewYearData[this.dataPrefix + this.dataKey] ??= {});
    ccDataRef[rowKey] = {};

    tableBody.append(this.generateRow(rowKey, data));
    tableBody
      .children(":last")
      .children()
      .each((_index, childElement) => {
        const child = $(childElement);
        if (!child.hasClass("noPrint")) {
          const colKey = this.columns[child.index()].db;
          ccDataRef[rowKey][colKey] = child.text();
        }
      });

    if (hashRow) {
      ccDataRef[rowKey].initialHash = ccDataRef[rowKey].editHash =
        this.quickRowHash(ccDataRef[rowKey]);
    }
    this.emit("rowCountChange");
    this.cc.checkSave(this.cc.quietFields);
    return rowKey;
  }

  /**
   * For checking for edits
   */
  public quickStringHash = (str: string) => {
    let hsh = 0;
    if (str.length) {
      for (let i = 0; i < str.length; ++i) {
        hsh = (hsh << 5) - hsh + str.charCodeAt(i);
        hsh |= 0;
      }
    }
    return hsh;
  };
  /**
   * For checking for edits
   */
  public quickRowHash = (row: Record<string, string>) => {
    return this.quickStringHash(
      Object.entries(row)
        .map(([k, v], i) => String(i + k + v))
        .join("")
    );
  };

  /**
   *  Generate the HTML for a row without altering the table or DOM.
   *  @param {string} rowKey - The key (id) for the new row.
   *  @param {object} data - The data to put in the row.
   *  @returns {string} HTML generated.
   */
  public generateRow(rowKey: string, data?: { [key: string]: any }): string {
    let str = `<tr id="${rowKey}">`;
    const editable =
      this.options.editable.allowEditingText &&
      !this.options.editable.disableEverything;
    const defaultCell = (inner: any, locked: boolean, styling: string) =>
      `<td ${editable && !locked ? "contenteditable " : ""}${this.options.richTextEnabled && !this.options.editable.disableEverything ? 'class="richTextEnabled"' : ""} style="${styling}">${inner}</td>`;

    const cellStyle = `word-wrap: anywhere; white-space: pre-wrap; overflow: auto; width:200px;${this.options.border ? `border: ${this.options.border};` : ""}`;
    const cell = (inner: any, locked: boolean) => {
      if (this.options.cellCreator != undefined) {
        const value = this.options.cellCreator(
          inner,
          locked,
          cellStyle,
          this.cc
        );
        return value ?? defaultCell(inner, locked, cellStyle);
      }
      return defaultCell(inner, locked, cellStyle);
    };

    if (
      this.options.removeRowButton === "left" &&
      !this.options.editable.disableEverything
    ) {
      str += `<td class="${this.baseClass}RemoveRow noPrint">${CCIcon("remove")}</td>`;
    }
    this.columns.forEach(({ db, hidden }) => {
      if (!hidden) {
        str += cell(data?.[db] || "", this.options.lockColumns.includes(db));
      }
    });
    if (
      this.options.removeRowButton === "right" &&
      !this.options.editable.disableEverything
    ) {
      str += `<td class="${this.baseClass}RemoveRow noPrint">${CCIcon("remove")}</td>`;
    }
    return str + "</tr>";
  }

  /**
   *  Add event listener for cell editing. Used internally.
   */
  private hookDataEdit(): void {
    this.container
      .off("input", `#${this.baseClass}Body td`)
      .on("input", `#${this.baseClass}Body td`, (event) => {
        if (this.options.editable.disableEverything) return;
        if (!this.options.editable.allowEditingText) return;
        // Target here is the cell the user is editing.
        const target = $(event.target);
        // Get the row
        const row = $(event.target).closest("tr").first();
        // Get the key/id
        const rowKey = row.attr("id");
        const data = this.cc.newDef.career[this.cc.viewYear].data;
        // Get which column we are editing
        const colKey = this.columns[target.closest("td").index()].db;
        if (this.options.lockColumns.includes(colKey)) return;
        // Set the data
        if (this.options.richTextEnabled) {
          data[this.dataPrefix + this.dataKey][rowKey][colKey] =
            tinymce.activeEditor.getContent({ format: "html" });
        } else {
          const val = target.is("select") ? target.val() : target.text();
          data[this.dataPrefix + this.dataKey][rowKey][colKey] = val;
        }
        this.emit("edit", {
          element: target,
          row: rowKey,
          column: colKey,
          value: target.text()
        });
        this.cc.checkSave(this.cc.quietFields);
      });
  }

  /**
   *  Add event listener for adding rows. Used internally.
   */
  private hookAddRowButton(): void {
    this.container
      .off("click", `.${this.baseClass}AddRow`)
      .on("click", `.${this.baseClass}AddRow`, () => {
        if (this.options.editable.disableEverything) return;
        if (!this.options.editable.allowAddingRows) return;
        const tableBody = $(`#${this.baseClass}Body`);
        if (tableBody.html().trim() == "<tr></tr>") tableBody.empty();
        const data = this.cc.newDef.career[this.cc.viewYear].data;
        // Generate new row's key
        const newRowKey = `${this.dataKey}-${tableBody.children().length}`;
        // If there is no data yet, define it
        if (data[this.dataPrefix + this.dataKey] == undefined)
          data[this.dataPrefix + this.dataKey] = {};
        // Set this key to a new object
        data[this.dataPrefix + this.dataKey][newRowKey] = {};
        // Append row
        tableBody.append(this.generateRow(newRowKey));
        this.refreshRichTextInstances();

        // Autofocus the first column in the last row
        $(`#${this.baseClass}Body tr:last-child`)
          .children(":first")
          .trigger("focus");
        this.emit("addRow", newRowKey);
        this.emit("rowCountChange");
      });
  }

  /**
   *  Add event listener for removing rows. Used internally.
   */
  private hookRemoveRowButtons(): void {
    this.container
      .off("click", `.${this.baseClass}RemoveRow`)
      .on("click", `.${this.baseClass}RemoveRow`, (event) => {
        if (this.options.editable.disableEverything) return;
        if (!this.options.editable.allowRemovingRows) return;
        const data = this.cc.newDef.career[this.cc.viewYear].data;
        // Get the row to delete's info
        const row = $(event.target).closest("tr").first();
        const rowId = row.attr("id");
        const rowNum = parseInt(rowId.split("-").pop());
        if (isNaN(rowNum)) return; // Just be safe
        //for each row
        $(`#${this.baseClass}Body`)
          .children()
          .each((_index, childElement) => {
            // Children here are rows in the table
            // Get the child's info
            const child = $(childElement);
            const childId = child.attr("id");
            const childNum = parseInt(childId.split("-").pop());
            if (isNaN(childNum)) return;
            // If the child's index/key/id is higher than the one we are deleting, we have to move it down.
            if (childNum > rowNum) {
              // Set new element id
              const newId = `${this.dataKey}-${childNum - 1}`;
              child.attr("id", newId);
              // Copy data over
              data[this.dataPrefix + this.dataKey][newId] =
                data[this.dataPrefix + this.dataKey][childId];
              // Delete old data
              delete data[this.dataPrefix + this.dataKey][childId];
            } else if (childNum == rowNum) {
              // If this is the row we are trying to remove
              this.cc.removeHTMLElement(child);
              // Delete the data
              delete data[this.dataPrefix + this.dataKey][childId];
            }
          });
        this.refreshRichTextInstances();
        this.emit("removeRow", row);
        this.emit("rowCountChange");
        this.cc.checkSave(this.cc.quietFields);
      });
  }
}
